<script>
    import { onMount } from 'svelte';
    import { EditorState } from '@codemirror/state';
    import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
    import { defaultKeymap, insertNewline, history, historyKeymap, undo, redo, undoDepth, redoDepth } from '@codemirror/commands';
    import { selectionTracker } from '../../../utils/editorSelectionTracker.js';
    import { undoStore } from '../../../stores/undoStore.js';

    // Props
    export let content = '';
    export let onContentChange = (newContent) => {};
    export let onFocus = () => {};
    export let extensions = [];
    export let ariaLabel = 'Code editor';

    // Internal state
    let editorElement;
    export let editorView;
    
    // Expose undo/redo functions
    export function performUndo() {
        if (editorView) {
            undo(editorView);
            updateUndoState();
        }
    }
    
    export function performRedo() {
        if (editorView) {
            redo(editorView);
            updateUndoState();
        }
    }
    
    function updateUndoState() {
        // Temporarily disabled - needs better implementation
        // This was causing issues by actually performing undo/redo operations
        return;
    }

    // Watch for content prop changes
    $: if (editorView && content !== undefined) {
        const currentDoc = editorView.state.doc.toString();
        if (content !== currentDoc) {
            console.log('BaseEditor - updating content from', currentDoc.length, 'to', content?.length || 0, 'chars');
            
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                try {
                    const transaction = editorView.state.update({
                        changes: {
                            from: 0,
                            to: editorView.state.doc.length,
                            insert: content || ''
                        }
                    });
                    editorView.dispatch(transaction);
                    console.log('BaseEditor - update successful, new length:', editorView.state.doc.length);
                } catch (error) {
                    console.error('BaseEditor - update failed:', error);
                }
            });
        }
    }

    onMount(() => {
        const startState = EditorState.create({
            doc: content,
            selection: { anchor: content.length },
            extensions: [
                // Base extensions that every editor needs
                lineNumbers(),
                EditorView.lineWrapping,
                history({ minDepth: 50 }),  // Limit to 50 undo steps to save memory
                keymap.of([
                    ...defaultKeymap, 
                    ...historyKeymap,
                    // Add Ctrl+Y for redo to match spec editor
                    { key: "Ctrl-y", run: redo, preventDefault: true },
                    { key: "Mod-y", run: redo, preventDefault: true }
                ]),
                highlightActiveLine(),
                // Removed explicit Enter handler - let extensions handle it
                EditorView.updateListener.of(update => {
                    if (update.docChanged) {
                        const newContent = update.state.doc.toString();
                        onContentChange(newContent);
                        updateUndoState(); // Update undo state when document changes
                    }
                    // Track selection changes for claims editor
                    if (update.selectionSet) {
                        const selection = update.state.selection.main;
                        selectionTracker.updateSelection('claims', selection);
                    }
                }),
                EditorView.domEventHandlers({
                    focus: () => {
                        onFocus();
                        return false;
                    }
                }),
                // Custom extensions passed from parent
                ...extensions
            ]
        });

        editorView = new EditorView({
            state: startState,
            parent: editorElement
        });
        
        // Set aria attributes on the CodeMirror content element
        const cmContent = editorElement.querySelector('.cm-content');
        if (cmContent) {
            cmContent.setAttribute('aria-label', ariaLabel);
        }

        return () => {
            if (editorView) {
                editorView.destroy();
            }
        };
    });
</script>

<div class="base-editor">
    <div 
        bind:this={editorElement} 
        class="editor"
    ></div>
</div>

<style>
    .base-editor {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
    }

    .editor {
        position: relative;
        flex: 1;
        overflow: hidden;
        background: transparent;
    }
</style>
